import { useState, useEffect, useCallback, useMemo } from "react";
import { Select } from "antd";
import { Link, useOutletContext } from "react-router-dom";
import {
  listFilesPage,
  deleteFile,
  reindexFile,
  updateFileMetadata,
} from "../../../services/files";
import type { FileItem } from "../../../services/files";
import UploadModal from "./components/UploadModal";
import {
  departments as departmentsApi,
  jobTypes as jobTypesApi,
  dataPermissions,
} from "../../../services/admin";
import { listProjects } from "../../../services/projects";
import { ensurePermission } from "../../../utils/permissions";
import { getIndexStatusLabel } from "../../../utils/fileStatus";
import { listWorkStages, type WorkStageItem } from "../../../services/workStages";
import { formatBeijingDateTime } from "../../../utils/datetime";
import RIcon from "../../../components/RIcon";

type SortKey = "name" | "size" | "uploadTime";
type SortDir = "asc" | "desc";

const fileTypeIcon: Record<string, { icon: string; color: string }> = {
  pdf: { icon: "ri-file-pdf-line", color: "text-red-500" },
  doc: { icon: "ri-file-word-line", color: "text-sky-500" },
  docx: { icon: "ri-file-word-line", color: "text-sky-500" },
  xls: { icon: "ri-file-excel-line", color: "text-green-600" },
  xlsx: { icon: "ri-file-excel-line", color: "text-green-600" },
  ppt: { icon: "ri-file-ppt-line", color: "text-orange-500" },
  pptx: { icon: "ri-file-ppt-line", color: "text-orange-500" },
};

const permissionLabels: Record<string, string> = {
  public: "全公司",
  departments: "指定部门",
  job_titles: "指定工种",
  users: "指定用户",
};

const permissionColor: Record<string, string> = {
  "全公司": "bg-emerald-100 text-emerald-700",
  "指定部门": "bg-sky-100 text-sky-700",
  "指定工种": "bg-amber-100 text-amber-700",
  "指定用户": "bg-violet-100 text-violet-700",
};

const indexStatusStyle: Record<string, { label: string; cls: string }> = {
  "已完成": { label: "已完成", cls: "bg-emerald-100 text-emerald-700" },
  "索引中": { label: "索引中", cls: "bg-sky-100 text-sky-700" },
  "索引失败": { label: "索引失败", cls: "bg-red-100 text-red-600" },
};


function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileExt(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() ?? "";
}

interface FileRecord {
  id: number;
  ownerId: number;
  name: string;
  type: string;
  size: string;
  sizeBytes: number;
  permission: string;
  uploadTime: string;
  uploadTimeLabel: string;
  indexStatus: "已完成" | "索引中" | "索引失败";
  jobTypeName: string;
  workStageName: string;
  projectName: string;
  category: string;
  departmentName: string;
  departmentId: number | null;
  dataPermissionRuleId: number | null;
  dataPermissionRuleIds: number[];
  jobTypeId: number | null;
  workStageId: number | null;
  projectId: number | null;
  downloadable: boolean;
}

function mapFileItem(item: FileItem): FileRecord {
  const ext = getFileExt(item.file_name);
  const statusLabel = getIndexStatusLabel(item.index_status);
  const permLabel = permissionLabels[item.permission_type] ?? item.permission_type;
  return {
    id: item.id,
    ownerId: item.owner_id,
    name: item.file_name,
    type: ext,
    size: formatBytes(item.file_size),
    sizeBytes: item.file_size,
    permission: item.data_permission_rule_name || permLabel,
    uploadTime: item.created_at,
    uploadTimeLabel: formatBeijingDateTime(item.created_at),
    indexStatus: statusLabel as FileRecord["indexStatus"],
    jobTypeName: item.job_type_name || "",
    workStageName: item.work_stage_name || "",
    projectName: item.project_name || "",
    category: item.category || "",
    departmentName: item.department_name || "",
    departmentId: item.department_id ?? null,
    dataPermissionRuleId: item.data_permission_rule_id ?? null,
    dataPermissionRuleIds: item.data_permission_rule_ids || (item.data_permission_rule_id ? [item.data_permission_rule_id] : []),
    jobTypeId: item.job_type_id,
    workStageId: item.work_stage_id,
    projectId: item.project_id,
    downloadable: item.downloadable ?? false,
  };
}

export default function FilesPage() {
  const { user } = useOutletContext<{ user: import("../../../services/auth").UserInfo | null }>();
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<"upload" | "subscription" | "all">("upload");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("uploadTime");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [batchDeleteOpen, setBatchDeleteOpen] = useState(false);
  const [batchPermOpen, setBatchPermOpen] = useState(false);
  const [batchRuleIds, setBatchRuleIds] = useState<number[]>([]);
  const [reindexingIds, setReindexingIds] = useState<Set<number>>(new Set());

  // Edit modal state
  const [editFile, setEditFile] = useState<FileRecord | null>(null);
  const [editDepartmentId, setEditDepartmentId] = useState<number | null>(null);
  const [editJobTypeId, setEditJobTypeId] = useState<number | null>(null);
  const [editProjectId, setEditProjectId] = useState<number | null>(null);
  const [editWorkStageId, setEditWorkStageId] = useState<number | null>(null);
  const [editCategory, setEditCategory] = useState("");
  const [editCustomCategory, setEditCustomCategory] = useState("");
  const [editDataPermissionRuleIds, setEditDataPermissionRuleIds] = useState<number[]>([]);
  const [editDownloadable, setEditDownloadable] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState<{ id: number; name: string }[]>([]);
  const [jobTypeOptions, setJobTypeOptions] = useState<{ id: number; name: string }[]>([]);
  const [stageOptions, setStageOptions] = useState<WorkStageItem[]>([]);
  const [projectOptions, setProjectOptions] = useState<{ id: number; name: string }[]>([]);
  const [dataPermissionRules, setDataPermissionRules] = useState<{ id: number; name: string }[]>([]);

  const PRESET_CATEGORIES = ["模板文件", "BOM", "质量标准", "作业指导书", "检验规程", "技术要求", "变更控制", "其他"];

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  const canDeleteRecord = useCallback(
    (file: FileRecord) => {
      if (!user) return false;
      if (user.is_superuser) return true;
      return file.ownerId === user.id;
    },
    [user]
  );

  const [totalCount, setTotalCount] = useState(0);
  const loadFiles = useCallback(async () => {
    try {
      const skip = (currentPage - 1) * pageSize;
      const { items, total } = await listFilesPage(skip, pageSize, {
        source: sourceFilter === "all" ? undefined : sourceFilter,
        search: search.trim() || undefined,
      });
      setFiles(items.map(mapFileItem));
      setTotalCount(total);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "加载文件失败");
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, sourceFilter, search]);

  useEffect(() => {
    loadFiles();
    departmentsApi.list().then((items) => setDepartmentOptions(items.map((d) => ({ id: d.id, name: d.name })))).catch(() => {});
    jobTypesApi.list().then(setJobTypeOptions).catch(() => {});
    listWorkStages().then(setStageOptions).catch(() => {});
    listProjects().then(setProjectOptions).catch(() => {});
    dataPermissions.listRules().then((rules) => setDataPermissionRules(rules.map((rule) => ({ id: rule.id, name: rule.name })))).catch(() => {});
  }, [loadFiles]);

  // 服务端已分页+筛选过；前端只对当前页做客户端排序
  const filtered = files
    .slice()
    .sort((a, b) => {
      if (sortKey === "size") {
        return sortDir === "asc" ? a.sizeBytes - b.sizeBytes : b.sizeBytes - a.sizeBytes;
      }
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  const filteredEditStageOptions = useMemo(
    () =>
      stageOptions.filter(
        (stage) => !editJobTypeId || !stage.job_type_id || stage.job_type_id === editJobTypeId
      ),
    [editJobTypeId, stageOptions]
  );

  useEffect(() => {
    setEditWorkStageId((current) => {
      if (current == null) return current;
      return filteredEditStageOptions.some((stage) => stage.id === current) ? current : null;
    });
  }, [filteredEditStageOptions]);

  // 服务端分页：当前页就是当前文件列表本身
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedFiles = filtered;
  const visibleIds = paginatedFiles.map((f) => f.id);
  const allChecked = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someChecked = visibleIds.some((id) => selected.has(id));

  useEffect(() => {
    setCurrentPage(1);
  }, [search, pageSize, sourceFilter]);

  useEffect(() => {
    if (currentPage !== safeCurrentPage) {
      setCurrentPage(safeCurrentPage);
    }
  }, [currentPage, safeCurrentPage]);

  useEffect(() => {
    if (
      editWorkStageId &&
      !filteredEditStageOptions.some((stage) => stage.id === editWorkStageId)
    ) {
      setEditWorkStageId(null);
    }
  }, [editWorkStageId, filteredEditStageOptions]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const toggleAll = () => {
    const next = new Set(selected);
    if (allChecked) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    setSelected(next);
  };

  const toggleOne = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const handleDelete = async (fileId: number) => {
    const target = files.find((file) => file.id === fileId);
    if (!target || !canDeleteRecord(target)) {
      showToast("你没有相关权限，请联系管理员");
      setDeleteId(null);
      return;
    }
    try {
      await deleteFile(fileId);
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      showToast("文件已删除");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "删除失败");
    }
    setDeleteId(null);
  };

  const handleBatchDelete = async () => {
    const ids = [...selected];
    const blocked = files.find((file) => ids.includes(file.id) && !canDeleteRecord(file));
    if (blocked) {
      setBatchDeleteOpen(false);
      showToast("你没有相关权限，请联系管理员");
      return;
    }
    let count = 0;
    for (const id of ids) {
      try {
        await deleteFile(id);
        count++;
      } catch {
        // continue with remaining
      }
    }
    showToast(`已删除 ${count} 个文件`);
    setSelected(new Set());
    setBatchDeleteOpen(false);
    // 删除整页文件后需要从服务端重新拉取（前端 filter 会让当前页变空）
    const remainingTotal = Math.max(0, totalCount - count);
    const newTotalPages = Math.max(1, Math.ceil(remainingTotal / pageSize));
    if (currentPage > newTotalPages) {
      setCurrentPage(newTotalPages);
    } else {
      await loadFiles();
    }
  };

  const handleBatchPerm = async () => {
    if (!ensurePermission(user, "files:write", showToast)) return;
    const ids = [...selected];
    let count = 0;
    const ruleIds = [...batchRuleIds];
    const ruleNames = ruleIds
      .map((rid) => dataPermissionRules.find((rule) => rule.id === rid)?.name)
      .filter(Boolean) as string[];
    const ruleLabel = ruleNames.length > 0 ? ruleNames.join("、") : "不绑定规则";
    for (const id of ids) {
      try {
        await updateFileMetadata(id, { data_permission_rule_ids: ruleIds });
        count++;
      } catch {
        // continue
      }
    }
    setFiles((prev) => prev.map((f) => selected.has(f.id) ? { ...f, dataPermissionRuleIds: ruleIds, dataPermissionRuleId: ruleIds[0] ?? null, permission: ruleLabel } : f));
    showToast(`已将 ${count} 个文件的数据权限修改为「${ruleLabel}」`);
    setSelected(new Set());
    setBatchPermOpen(false);
    setBatchRuleIds([]);
    // 重新拉列表，确保显示的是 DB 真正保存的规则（而不是前端乐观更新）
    await loadFiles();
  };

  const handleReindex = async (fileId: number, fileName: string) => {
    if (!ensurePermission(user, "files:write", showToast)) return;
    setReindexingIds((prev) => new Set([...prev, fileId]));
    setFiles((prev) => prev.map((f) => f.id === fileId ? { ...f, indexStatus: "索引中" as const } : f));
    showToast(`「${fileName}」正在重新索引...`);
    try {
      await reindexFile(fileId);
      showToast(`「${fileName}」索引任务已提交`);
    } catch {
      showToast(`「${fileName}」索引请求失败`);
    } finally {
      setReindexingIds((prev) => { const n = new Set(prev); n.delete(fileId); return n; });
    }
  };

  const openEditModal = (file: FileRecord) => {
    if (!ensurePermission(user, "files:write", showToast)) return;
    setEditFile(file);
    setEditDepartmentId(file.departmentId);
    setEditJobTypeId(file.jobTypeId);
    setEditProjectId(file.projectId);
    setEditWorkStageId(file.workStageId);
    if (file.category && !PRESET_CATEGORIES.includes(file.category)) {
      setEditCategory("custom");
      setEditCustomCategory(file.category);
    } else {
      setEditCategory(file.category);
      setEditCustomCategory("");
    }
    setEditDataPermissionRuleIds(file.dataPermissionRuleIds);
    setEditDownloadable(file.downloadable);
  };

  const handleEditSave = async () => {
    if (!editFile) return;
    if (!ensurePermission(user, "files:write", showToast)) return;
    setEditSaving(true);
    try {
      const savedCategory = editCategory === "custom" ? editCustomCategory.trim() : editCategory;
      await updateFileMetadata(editFile.id, {
        department_id: editDepartmentId ?? undefined,
        job_type_id: editJobTypeId ?? undefined,
        project_id: editProjectId ?? undefined,
        work_stage_id: editWorkStageId ?? undefined,
        category: savedCategory || undefined,
        data_permission_rule_ids: editDataPermissionRuleIds,
        downloadable: editDownloadable,
      });
      // Update local state
      const deptName = departmentOptions.find((d) => d.id === editDepartmentId)?.name || "";
      const jtName = jobTypeOptions.find((j) => j.id === editJobTypeId)?.name || "";
      const pjName = projectOptions.find((p) => p.id === editProjectId)?.name || "";
      const wsName = stageOptions.find((s) => s.id === editWorkStageId)?.name || "";
      const permissionName = editDataPermissionRuleIds.length > 0
        ? editDataPermissionRuleIds
            .map((rid) => dataPermissionRules.find((r) => r.id === rid)?.name)
            .filter(Boolean)
            .join("、") || (editFile.permission || "不绑定规则")
        : (editFile.permission || "不绑定规则");
      setFiles((prev) =>
        prev.map((f) =>
          f.id === editFile.id
            ? {
                ...f,
                departmentName: deptName,
                departmentId: editDepartmentId,
                jobTypeName: jtName,
                workStageName: wsName,
                projectName: pjName,
                category: savedCategory,
                dataPermissionRuleId: editDataPermissionRuleIds[0] ?? null,
                dataPermissionRuleIds: editDataPermissionRuleIds,
                permission: permissionName,
                jobTypeId: editJobTypeId,
                workStageId: editWorkStageId,
                projectId: editProjectId,
                downloadable: editDownloadable,
              }
            : f
        )
      );
      showToast("文件信息已更新");
      setEditFile(null);
      // 重新拉列表，确保显示的是 DB 真正保存的规则
      await loadFiles();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "更新失败");
    } finally {
      setEditSaving(false);
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) =>
    sortKey === col ? (
      <i className={`ri-arrow-${sortDir === "asc" ? "up" : "down"}-line text-[#0EA5A0] text-xs`} />
    ) : (
      <RIcon name="arrow-up-down-line" className="text-gray-300 text-xs" />
    );

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[60] bg-[#1A2B3C] text-white text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 whitespace-nowrap">
          <RIcon name="checkbox-circle-line" className="text-[#AAFF45]" />{toast}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#1A2B3C]">文件管理</h2>
          <p className="text-xs text-[#718096] mt-0.5">管理企业知识文档，支持AI智能索引</p>
        </div>
        <button
          onClick={() => ensurePermission(user, "files:write", showToast) && setUploadOpen(true)}
          className="flex items-center gap-2 px-4 h-9 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <RIcon name="upload-cloud-line" />上传文件
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3">
        <div className="flex items-center gap-2 flex-1 border border-gray-200 rounded-lg px-3 h-9 focus-within:border-[#0EA5A0] transition-colors">
          <RIcon name="search-line" className="text-[#718096] text-sm" />
          <input
            type="text"
            placeholder="搜索文件名..."
            className="flex-1 text-sm outline-none placeholder:text-gray-400"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-[#718096]">
            <span>来源</span>
            <Select
              size="small"
              value={sourceFilter}
              onChange={(v) => setSourceFilter(v as "upload" | "subscription" | "all")}
              style={{ width: 120 }}
              options={[
                { value: "upload", label: "用户上传" },
                { value: "subscription", label: "订阅报告" },
                { value: "all", label: "全部" },
              ]}
            />
          </div>
          <span className="text-xs text-[#718096] whitespace-nowrap">共 {totalCount} 个文件</span>
          <div className="flex items-center gap-2 text-xs text-[#718096]">
            <span>每页</span>
            <Select
              size="small"
              style={{ width: 80 }}
              value={pageSize}
              onChange={(v) => setPageSize(Number(v))}
              options={[20, 50, 100].map((size) => ({ value: size, label: String(size) }))}
            />
          </div>
        </div>
      </div>

      {/* Batch Action Bar */}
      <div className={`bg-[#1A2B3C] rounded-xl px-4 flex items-center gap-3 transition-all duration-300 overflow-hidden ${selected.size > 0 ? "py-2.5 max-h-16 opacity-100" : "max-h-0 opacity-0 py-0"}`}>
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <RIcon name="checkbox-circle-line" className="text-[#AAFF45] text-base" />
        </div>
        <span className="text-white text-sm font-medium flex-1">已选 <span className="text-[#AAFF45] font-bold">{selected.size}</span> 个文件</span>
        <button onClick={() => { if (ensurePermission(user, "files:write", showToast)) { setBatchRuleIds([]); setBatchPermOpen(true); } }} className="flex items-center gap-1.5 px-3 h-8 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap">
          <RIcon name="lock-line" className="text-sm" />批量选择数据权限
        </button>
        <button
          onClick={() => {
            const ids = [...selected];
            const blocked = files.find((file) => ids.includes(file.id) && !canDeleteRecord(file));
            if (blocked) {
              showToast("你没有相关权限，请联系管理员");
              return;
            }
            setBatchDeleteOpen(true);
          }}
          className="flex items-center gap-1.5 px-3 h-8 bg-red-500/80 hover:bg-red-500 text-white rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap"
        >
          <RIcon name="delete-bin-line" className="text-sm" />批量删除
        </button>
        <button onClick={() => setSelected(new Set())} className="w-7 h-7 flex items-center justify-center rounded-lg text-white/50 hover:text-white hover:bg-white/10 cursor-pointer">
          <RIcon name="close-line" className="text-base" />
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center">
            <RIcon name="loader-4-line" className="animate-spin text-3xl text-[#0EA5A0] block mb-3" />
            <p className="text-sm text-[#718096]">加载中...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <RIcon name="folder-open-line" className="text-4xl text-gray-300 block mb-3" />
            <p className="text-sm text-[#718096] mb-1">暂无文件</p>
            <p className="text-xs text-[#718096]/60">点击右上角「上传文件」开始上传</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-[#F4F8FB]">
                  <th className="pl-4 pr-2 py-3 w-10">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <input type="checkbox" checked={allChecked} ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }} onChange={toggleAll} className="w-4 h-4 rounded border-gray-300 accent-[#0EA5A0] cursor-pointer" />
                    </div>
                  </th>
                  {[
                    { label: "文件名", key: "name" as SortKey, sortable: true },
                    { label: "类型", key: null },
                    { label: "部门", key: null },
                    { label: "工种", key: null },
                    { label: "业务阶段", key: null },
                    { label: "项目", key: null },
                    { label: "分类", key: null },
                    { label: "大小", key: "size" as SortKey, sortable: true },
                    { label: "权限", key: null },
                    { label: "索引状态", key: null },
                    { label: "上传时间", key: "uploadTime" as SortKey, sortable: true },
                    { label: "操作", key: null },
                  ].map((col, i) => (
                    <th
                      key={i}
                      className={`text-left px-4 py-3 text-xs font-semibold text-[#718096] whitespace-nowrap ${col.sortable ? "cursor-pointer hover:text-[#0EA5A0]" : ""}`}
                      onClick={() => col.sortable && col.key && handleSort(col.key)}
                    >
                      <span className="flex items-center gap-1">{col.label}{col.sortable && col.key && <SortIcon col={col.key} />}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedFiles.map((file) => {
                  const typeInfo = fileTypeIcon[file.type] || { icon: "ri-file-line", color: "text-gray-400" };
                  const statusInfo = indexStatusStyle[file.indexStatus] || indexStatusStyle["索引中"];
                  const isSelected = selected.has(file.id);
                  return (
                    <tr key={file.id} className={`border-b border-gray-50 transition-colors ${isSelected ? "bg-[#0EA5A0]/5" : "hover:bg-[#F4F8FB]/50"}`}>
                      <td className="pl-4 pr-2 py-3">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <input type="checkbox" checked={isSelected} onChange={() => toggleOne(file.id)} className="w-4 h-4 rounded border-gray-300 accent-[#0EA5A0] cursor-pointer" />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/workbench/files/${file.id}`} className="text-[#0EA5A0] hover:underline font-medium truncate max-w-48 block">{file.name}</Link>
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${typeInfo.icon} ${typeInfo.color} text-base`} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#4A5568] text-xs whitespace-nowrap">{file.departmentName || <span className="text-gray-300">-</span>}</td>
                      <td className="px-4 py-3 text-[#4A5568] text-xs whitespace-nowrap">{file.jobTypeName || <span className="text-gray-300">-</span>}</td>
                      <td className="px-4 py-3 text-[#4A5568] text-xs whitespace-nowrap">{file.workStageName || <span className="text-gray-300">-</span>}</td>
                      <td className="px-4 py-3 text-[#4A5568] text-xs whitespace-nowrap">{file.projectName || <span className="text-gray-300">-</span>}</td>
                      <td className="px-4 py-3 text-[#4A5568] text-xs whitespace-nowrap">{file.category ? <span className="bg-gray-100 text-[#4A5568] px-2 py-0.5 rounded-full">{file.category}</span> : <span className="text-gray-300">-</span>}</td>
                      <td className="px-4 py-3 text-[#718096] whitespace-nowrap">{file.size}</td>
                      <td className="px-4 py-3">
                        {(() => {
                          // 多规则：从 dataPermissionRules（已 load 全量）按 id 拼名字；若无 ids 则降级到 permission 字段
                          const ids = file.dataPermissionRuleIds || [];
                          const names = ids.map((rid) => dataPermissionRules.find((r) => r.id === rid)?.name).filter(Boolean) as string[];
                          if (names.length > 0) {
                            return (
                              <div className="flex flex-wrap gap-1 max-w-xs">
                                {names.map((n) => (
                                  <span key={n} className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 whitespace-nowrap">{n}</span>
                                ))}
                              </div>
                            );
                          }
                          return <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${permissionColor[file.permission] || "bg-gray-100 text-gray-500"}`}>{file.permission}</span>;
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${statusInfo.cls}`}>{statusInfo.label}</span>
                      </td>
                      <td className="px-4 py-3 text-[#718096] whitespace-nowrap">{file.uploadTimeLabel}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEditModal(file)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-[#718096] hover:text-[#0EA5A0] transition-colors cursor-pointer"
                            title="编辑信息"
                          >
                            <RIcon name="edit-line" className="text-sm" />
                          </button>
                          <button
                            onClick={() => handleReindex(file.id, file.name)}
                            disabled={reindexingIds.has(file.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-[#718096] hover:text-[#0EA5A0] transition-colors cursor-pointer disabled:opacity-40"
                            title="重新索引"
                          >
                            <RIcon name="refresh-line" className={`text-sm ${reindexingIds.has(file.id) ? "animate-spin" : ""}`} />
                          </button>
                          <button
                            onClick={() => {
                              if (!canDeleteRecord(file)) {
                                showToast("你没有相关权限，请联系管理员");
                                return;
                              }
                              setDeleteId(file.id);
                            }}
                            disabled={!canDeleteRecord(file)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#718096] hover:text-red-500 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#718096]"
                            title="删除文件"
                          >
                            <RIcon name="delete-bin-line" className="text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 bg-[#F8FAFC] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-xs text-[#718096]">
              显示第 <span className="font-medium text-[#1A2B3C]">{(safeCurrentPage - 1) * pageSize + 1}</span>
              {" "}-{" "}
              <span className="font-medium text-[#1A2B3C]">{Math.min(safeCurrentPage * pageSize, totalCount)}</span>
              {" "}条，共 <span className="font-medium text-[#1A2B3C]">{totalCount}</span> 条
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={safeCurrentPage <= 1}
                className="h-8 px-3 border border-gray-200 rounded-lg text-xs text-[#4A5568] hover:border-[#0EA5A0] hover:text-[#0EA5A0] disabled:opacity-40 cursor-pointer whitespace-nowrap"
              >
                上一页
              </button>
              <span className="text-xs text-[#718096] whitespace-nowrap">
                第 <span className="font-medium text-[#1A2B3C]">{safeCurrentPage}</span> / {totalPages} 页
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={safeCurrentPage >= totalPages}
                className="h-8 px-3 border border-gray-200 rounded-lg text-xs text-[#4A5568] hover:border-[#0EA5A0] hover:text-[#0EA5A0] disabled:opacity-40 cursor-pointer whitespace-nowrap"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {uploadOpen && (
        <UploadModal
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            showToast("文件上传成功，等待AI索引");
            loadFiles();
          }}
        />
      )}

      {/* Single Delete */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RIcon name="delete-bin-line" className="text-2xl text-red-500" />
            </div>
            <h3 className="text-base font-semibold text-[#1A2B3C] text-center mb-1">确认删除</h3>
            <p className="text-sm text-[#718096] text-center mb-6">删除后文件将从系统中移除，此操作不可撤销</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 h-10 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Delete */}
      {batchDeleteOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RIcon name="delete-bin-line" className="text-2xl text-red-500" />
            </div>
            <h3 className="text-base font-semibold text-[#1A2B3C] text-center mb-1">批量删除确认</h3>
            <p className="text-sm text-[#718096] text-center mb-6">即将删除 <span className="text-red-500 font-semibold">{selected.size}</span> 个文件，此操作不可撤销</p>
            <div className="flex gap-3">
              <button onClick={() => setBatchDeleteOpen(false)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleBatchDelete} className="flex-1 h-10 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">确认删除 {selected.size} 个</button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Permission */}
      {batchPermOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-[#1A2B3C]">批量修改权限</h3>
              <button onClick={() => setBatchPermOpen(false)} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer"><RIcon name="close-line" className="text-base" /></button>
            </div>
            <p className="text-sm text-[#718096] mb-4">为已选 <span className="text-[#0EA5A0] font-semibold">{selected.size}</span> 个文件统一选择数据权限规则（可多选，规则之间为并集）</p>
            <div className="space-y-3 mb-6">
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">数据权限规则（可多选；不选 = 不绑定规则）</label>
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  placeholder="搜索并选择规则"
                  style={{ width: "100%" }}
                  value={batchRuleIds}
                  onChange={(v) => setBatchRuleIds(v as number[])}
                  optionFilterProp="label"
                  options={dataPermissionRules.map((rule) => ({ value: rule.id, label: rule.name }))}
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setBatchPermOpen(false)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleBatchPerm} className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">确认修改</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit File Metadata Modal */}
      {editFile && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">编辑文件信息</h3>
              <button onClick={() => setEditFile(null)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer"><RIcon name="close-line" className="text-[#718096]" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-xs text-[#718096] mb-1">文件名</p>
                <p className="text-sm font-medium text-[#1A2B3C] truncate">{editFile.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">部门</label>
                <Select
                  showSearch allowClear placeholder="请选择部门" style={{ width: "100%" }}
                  value={editDepartmentId ?? undefined}
                  onChange={(v) => setEditDepartmentId((v as number | undefined) ?? null)}
                  optionFilterProp="label"
                  options={departmentOptions.map((d) => ({ value: d.id, label: d.name }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">工种</label>
                <Select
                  showSearch allowClear placeholder="请选择工种" style={{ width: "100%" }}
                  value={editJobTypeId ?? undefined}
                  onChange={(v) => setEditJobTypeId((v as number | undefined) ?? null)}
                  optionFilterProp="label"
                  options={jobTypeOptions.map((jt) => ({ value: jt.id, label: jt.name }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">业务阶段</label>
                <Select
                  showSearch allowClear
                  placeholder={editJobTypeId ? "请选择阶段" : "请先选择工种"}
                  style={{ width: "100%" }}
                  disabled={!editJobTypeId}
                  value={editWorkStageId ?? undefined}
                  onChange={(v) => setEditWorkStageId((v as number | undefined) ?? null)}
                  optionFilterProp="label"
                  options={filteredEditStageOptions.map((s) => ({ value: s.id, label: s.name }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">所属项目</label>
                <Select
                  showSearch allowClear placeholder="请选择项目" style={{ width: "100%" }}
                  value={editProjectId ?? undefined}
                  onChange={(v) => setEditProjectId((v as number | undefined) ?? null)}
                  optionFilterProp="label"
                  options={projectOptions.map((p) => ({ value: p.id, label: p.name }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">数据权限规则（可多选；规则之间为并集；不选 = 不绑定）</label>
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  placeholder="搜索并选择规则"
                  style={{ width: "100%" }}
                  value={editDataPermissionRuleIds}
                  onChange={(v) => setEditDataPermissionRuleIds(v as number[])}
                  optionFilterProp="label"
                  options={dataPermissionRules.map((rule) => ({ value: rule.id, label: rule.name }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">文件分类</label>
                <Select
                  showSearch allowClear placeholder="请选择分类" style={{ width: "100%" }}
                  value={editCategory || undefined}
                  onChange={(v) => {
                    const next = (v ?? "") as string;
                    setEditCategory(next);
                    if (next !== "custom") setEditCustomCategory("");
                  }}
                  optionFilterProp="label"
                  options={[
                    ...PRESET_CATEGORIES.map((c) => ({ value: c, label: c })),
                    { value: "custom", label: "自定义分类..." },
                  ]}
                />
                {editCategory === "custom" && (
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0] mt-2"
                    value={editCustomCategory}
                    onChange={(e) => setEditCustomCategory(e.target.value)}
                    placeholder="输入自定义分类"
                  />
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="accent-[#0EA5A0] w-4 h-4"
                    checked={editDownloadable}
                    onChange={(e) => setEditDownloadable(e.target.checked)}
                  />
                  <span className="text-sm text-[#1A2B3C]">允许下载</span>
                  <span className="text-xs text-[#718096]">（取消后他人无法在详情页下载该文件）</span>
                </label>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setEditFile(null)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                <button onClick={handleEditSave} disabled={editSaving} className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap">
                  {editSaving ? <><RIcon name="loader-4-line" className="animate-spin" />保存中...</> : "保存"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
